import { autoserialize, autoserializeAs } from 'cerialize';
import * as _ from 'lodash';

import { Contact } from './contact';
import { CourierType } from './courier-type';
import { Pricing } from './pricing';
import { SponsorshipAlgorithm } from './sponsorship-algorithm';

export enum SponsorshipState {
  ACTIVE = 'active',
  PAUSED = 'paused',
  DISABLED = 'disabled',
}
export enum SponsorshipAlgorithmVersion {
  STATIC_V1 = 'static_v1',
  EFFICIENT_V1 = 'efficient_v1',
  FIXED = 'fixed',
}

export enum SponsorshipStripeStatus {
  REQUIRES_PAYMENT_METHOD = 'requires_payment_method',
  SUCCESS = 'succeeded',
}
export enum StripePaymentMethodType {
  CUSTOMER_BALANCE = 'customer_balance',
  US_BANK_ACCOUNT = 'us_bank_account',
  CARD = 'card',
}

export class Sponsorship implements ISponsorship {
  @autoserializeAs('charity_id') charityId?: string;
  @autoserialize id: string;
  @autoserialize zips?: string[];
  @autoserialize state: SponsorshipState;
  @autoserialize amount?: number;
  @autoserialize algorithm?: SponsorshipAlgorithm;
  @autoserializeAs('max_small') maxSmall?: number;
  @autoserializeAs('stripe_status')
  stripeStatus: SponsorshipStripeStatus | null;
  @autoserializeAs('algorithm_version')
  algorithmVersion?: SponsorshipAlgorithmVersion;
  @autoserializeAs('algorithm_min_quote') algorithmMinQuote?: number;
  @autoserializeAs('algorithm_max_discount') algorithmMaxDiscount?: number;
  @autoserializeAs('email_notification') emailNotification?: boolean;
  @autoserializeAs('discount_all_owners') discountAllOwners?: boolean;
  @autoserializeAs('billing_contact') billingContact?: Contact;
  @autoserialize payment_day?: number;
  @autoserializeAs('donor_percentage') donorPercentage?: number;
  @autoserializeAs('fixed_percent') fixedPercent?: number;
  @autoserializeAs('buffer_percent') bufferPercent?: number;
  @autoserializeAs('restricted_couriers') restrictedCouriers?: CourierType[];
  @autoserialize spend_limit?: number;
  @autoserialize low_account_balance?: number;
  @autoserialize auto_pay?: boolean;
  @autoserialize stripe_invoice?: string;
  @autoserialize stripe_invoice_status?: string;
  @autoserialize stripe_payment_method_type?: string;
  @autoserialize ledger?: {
    available_balance?: number;
  };

  constructor(payload?: ISponsorship) {
    this.charityId = payload?.charityId;
    this.id = payload?.id;
    this.zips = payload?.zips;
    this.state = payload?.state;
    this.amount = payload?.amount;
    this.maxSmall = payload?.maxSmall;
    this.stripeStatus = payload?.stripeStatus;
    this.algorithmVersion = payload?.algorithmVersion;
    this.algorithmMinQuote = payload?.algorithmMinQuote;
    this.algorithmMaxDiscount = payload?.algorithmMaxDiscount;
    this.emailNotification = payload?.emailNotification;
    this.discountAllOwners = payload?.discountAllOwners;
    this.billingContact = payload?.billingContact ? new Contact(payload.billingContact) : undefined;
    this.payment_day = payload?.payment_day;
    this.fixedPercent = payload?.fixedPercent;
    this.donorPercentage = payload?.donorPercentage;
    this.bufferPercent = payload?.bufferPercent;
    this.restrictedCouriers = payload?.restrictedCouriers;
    this.spend_limit = payload?.spend_limit;
    this.low_account_balance = payload?.low_account_balance;
    this.auto_pay = payload?.auto_pay;
    this.stripe_invoice = payload?.stripe_invoice;
    this.stripe_invoice_status = payload?.stripe_invoice_status;
    this.stripe_payment_method_type = payload?.stripe_payment_method_type;
    this.ledger = payload?.ledger;
  }

  public static OnSerialized(instance: Sponsorship, json: any): void {
    json.amount = json.amount !== null ? Pricing.formatPrice(json.amount * 100) : null;
    if (json.ledger) {
      json.ledger.available_balance =
        json.ledger.available_balance !== null ? Pricing.formatPrice(json.ledger.available_balance * 100) : null;
    }
    json.low_account_balance = json.low_account_balance !== null ? Pricing.formatPrice(json.low_account_balance * 100) : null;
    json.max_small = json.max_small !== null ? Pricing.formatPrice(json.max_small * 100) : null;
    json.algorithm_min_quote = json.algorithm_min_quote !== null ? Pricing.formatPrice(json.algorithm_min_quote * 100) : null;
    json.algorithm_max_discount = json.algorithm_max_discount !== null ? Pricing.formatPrice(json.algorithm_max_discount * 100) : null;

    json.donor_percentage = !_.isNil(json.donor_percentage) ? json.donor_percentage / 100 : undefined;
    json.fixed_percent = !_.isNil(json.fixed_percent) ? json.fixed_percent / 100 : undefined;
    json.buffer_percent = !_.isNil(json.buffer_percent) ? json.buffer_percent / 100 : undefined;

    json.spend_limit = json.spend_limit !== null ? Pricing.formatPrice(json.spend_limit * 100) : null;
  }

  public static OnDeserialized(instance: Sponsorship): void {
    instance.amount = instance.amount !== null ? Pricing.formatPrice(instance.amount / 100) : null;
    if (instance.ledger)
      instance.ledger.available_balance =
        instance.ledger.available_balance !== null ? Pricing.formatPrice(instance.ledger.available_balance / 100) : null;
    instance.low_account_balance = instance.low_account_balance !== null ? Pricing.formatPrice(instance.low_account_balance / 100) : null;
    instance.maxSmall = instance.maxSmall !== null ? Pricing.formatPrice(instance.maxSmall / 100) : null;
    instance.algorithmMaxDiscount =
      instance.algorithmMaxDiscount !== null ? Pricing.formatPrice(instance.algorithmMaxDiscount / 100) : null;
    instance.algorithmMinQuote = instance.algorithmMinQuote !== null ? Pricing.formatPrice(instance.algorithmMinQuote / 100) : null;

    const donorPercentage = instance.donorPercentage || instance.algorithm?.donor_percentage;
    const fixedPercent = instance.fixedPercent || instance.algorithm?.fixed_percent;

    instance.donorPercentage = !_.isNil(donorPercentage) ? donorPercentage * 100 : null;
    instance.fixedPercent = !_.isNil(fixedPercent) ? fixedPercent * 100 : null;
    instance.bufferPercent = !_.isNil(instance.bufferPercent) ? instance.bufferPercent * 100 : null;

    instance.spend_limit = instance.spend_limit !== null ? Pricing.formatPrice(instance.spend_limit / 100) : null;
  }
}
export interface ISponsorship {
  charityId?: string;
  id?: string;
  zips?: string[];
  state?: SponsorshipState;
  amount?: number;
  maxSmall?: number;
  stripeStatus?: SponsorshipStripeStatus;
  algorithmVersion?: SponsorshipAlgorithmVersion;
  algorithmMinQuote?: number;
  algorithmMaxDiscount?: number;
  emailNotification?: boolean;
  discountAllOwners?: boolean;
  billingContact?: Contact;
  payment_day?: number;
  fixedPercent?: number;
  donorPercentage?: number;
  algorithmFixedPercent?: number;
  bufferPercent?: number;
  restrictedCouriers?: CourierType[];
  spend_limit?: number;
  low_account_balance?: number;
  auto_pay?: boolean;
  stripe_invoice?: string;
  stripe_invoice_status?: string;
  stripe_payment_method_type?: string;
  ledger?: {
    available_balance?: number;
  };
}
